import { useMemo } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { AppState } from "@Store/index";
import { extractFloorPlanRoomTypes, FloorPlanRoomType } from "../utils/floorPlanHelpers";

/**
 * Gets area string from meta object, handling min_area/max_area patterns.
 */
const getAreaFromMeta = (meta: any): string | null => {
  if (!meta) return null;
  const areaUnit = meta.area_unit || "sqft";
  if (meta.area) {
    return `${meta.area} ${areaUnit}`;
  }
  if (meta.min_area && meta.max_area) {
    return `${meta.min_area} - ${meta.max_area} ${areaUnit}`;
  }
  if (meta.min_area) {
    return `${meta.min_area}+ ${areaUnit}`;
  }
  if (meta.max_area) {
    return `${meta.max_area} ${areaUnit}`;
  }
  return null;
};

/**
 * Reads floor plan room types from propertyPageData.roomTypes.data.
 * This is the single source of truth for floor plan images.
 */
const useFloorPlanData = () => {
  const { roomTypesData, readyStatus } = useSelector(
    ({ propertyPageData }: AppState) => ({
      roomTypesData: propertyPageData.roomTypes?.data || [],
      readyStatus: propertyPageData.readyStatus,
    }),
    shallowEqual,
  );

  // Extract room types that have floor_plan images
  const baseRoomTypes = useMemo(() => extractFloorPlanRoomTypes(roomTypesData), [roomTypesData]);

  // Enrich room types with area/floor/facing from tenancy-level meta
  // eslint-disable-next-line arrow-body-style
  const roomTypes: FloorPlanRoomType[] = useMemo(() => {
    return baseRoomTypes.map((rt) => {
      const detailedRoomType = roomTypesData.find((detailed: any) => String(detailed.id) === rt.id);
      if (!detailedRoomType) return rt;

      const roomTypeMeta = detailedRoomType.meta || {};
      let area = getAreaFromMeta(roomTypeMeta);
      let floor = roomTypeMeta.floor || null;
      let facing = roomTypeMeta.facing || null;

      // If not available at room type level, try first tenancy
      if (!area || !floor || !facing) {
        const tenancies =
          detailedRoomType.active_children_with_filters || detailedRoomType.active_children || [];
        const firstTenancy = tenancies[0];
        if (firstTenancy?.meta) {
          if (!area) area = getAreaFromMeta(firstTenancy.meta);
          if (!floor) floor = firstTenancy.meta.floor || null;
          if (!facing) facing = firstTenancy.meta.facing || null;
        }
      }

      return {
        ...rt,
        area: area || rt.area,
        floor: floor || rt.floor,
        facing: facing || rt.facing,
      };
    });
  }, [baseRoomTypes, roomTypesData]);

  const hasFloorPlans = roomTypes.length > 0;

  return { roomTypes, hasFloorPlans, readyStatus };
};

export default useFloorPlanData;
